/**
 * Date Utilities
 */

/**
 * Format a Date object as YYYY-MM-DD in local timezone
 */
function formatDateLocal(date: Date): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

/**
 * Get today's date in YYYY-MM-DD format (local timezone)
 */
export function getToday(): string {
  return formatDateLocal(new Date());
}

/**
 * Get yesterday's date in YYYY-MM-DD format (local timezone)
 */
export function getYesterday(): string {
  const date = new Date();
  date.setDate(date.getDate() - 1);
  return formatDateLocal(date);
}

/**
 * Format a date string for display
 */
export function formatDate(dateStr: string, options?: Intl.DateTimeFormatOptions): string {
  const date = new Date(dateStr + 'T00:00:00');
  return date.toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'long',
    day: 'numeric',
    ...options,
  });
}

/**
 * Format a date as relative text (Today, Yesterday, or formatted date)
 */
export function formatRelativeDate(dateStr: string): string {
  const today = getToday();
  const yesterday = getYesterday();

  if (dateStr === today) return 'Today';
  if (dateStr === yesterday) return 'Yesterday';

  return formatDate(dateStr);
}

/**
 * Get the start of the week (Monday) for a given date
 */
export function getWeekStart(dateStr: string): string {
  const date = new Date(dateStr + 'T00:00:00');
  const day = date.getDay();
  const diff = date.getDate() - day + (day === 0 ? -6 : 1); // Adjust for Sunday
  date.setDate(diff);
  return formatDateLocal(date);
}

/**
 * Get an array of dates for a week starting from the given date
 */
export function getWeekDates(startDate: string): string[] {
  const dates: string[] = [];
  const start = new Date(startDate + 'T00:00:00');

  for (let i = 0; i < 7; i++) {
    const date = new Date(start);
    date.setDate(start.getDate() + i);
    dates.push(formatDateLocal(date));
  }

  return dates;
}

/**
 * Check if two dates are consecutive
 */
export function areConsecutiveDates(date1: string, date2: string): boolean {
  const d1 = new Date(date1 + 'T00:00:00');
  const d2 = new Date(date2 + 'T00:00:00');
  const diffTime = Math.abs(d2.getTime() - d1.getTime());
  const diffDays = diffTime / (1000 * 60 * 60 * 24);
  return diffDays === 1;
}

/**
 * Get days between two dates
 */
export function getDaysBetween(date1: string, date2: string): number {
  const d1 = new Date(date1 + 'T00:00:00');
  const d2 = new Date(date2 + 'T00:00:00');
  const diffTime = d2.getTime() - d1.getTime();
  return Math.floor(diffTime / (1000 * 60 * 60 * 24));
}
